import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { X, Check, Printer, Download, Share2, Clock, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { printReceiptSmart } from "../lib/btPrinter";
import html2canvas from "html2canvas";

// Consistent logo size for receipts (auto-resize)
const LOGO_MAX_WIDTH = 80;
const LOGO_MAX_HEIGHT = 80;

/**
 * Preload and cache image as base64 to ensure html2canvas can render it
 */
const preloadImageAsBase64 = (src) => {
  return new Promise((resolve, reject) => {
    // If already base64, just load it
    if (src.startsWith('data:')) {
      const img = new Image();
      img.onload = () => resolve({ img, base64: src });
      img.onerror = (e) => reject(e);
      img.src = src;
      return;
    }
    
    // For URL images, convert to base64
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const base64 = canvas.toDataURL('image/png');
        resolve({ img, base64 });
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = (e) => reject(e);
    img.src = src;
  });
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(amount || 0);

/**
 * Parse PLN Token SN string into structured data
 */
const parsePlnTokenSN = (sn) => {
  if (!sn || !sn.includes("/")) {
    return { raw: sn, token: sn, nama: "", tarif: "", daya: "", kwh: "" };
  }
  
  const parts = sn.split("/");
  const token = parts[0] || "";
  const nama = parts[1] || "";
  let tarif = "", daya = "", kwh = "";
  
  if (parts.length >= 4) {
    tarif = parts[2] || "";
    const remaining = parts.slice(3).join("/");
    const dayaMatch = remaining.match(/(\d+)\s*VA/i);
    const kwhMatch = remaining.match(/([\d,\.]+)\s*KWH/i);
    if (dayaMatch) daya = dayaMatch[1] + " VA";
    if (kwhMatch) kwh = kwhMatch[1].replace(",", ".") + " kWh";
    if (!daya && !kwh) {
      if (parts[3]) daya = parts[3];
      if (parts[4]) kwh = parts[4];
    }
  }
  
  return { raw: sn, token, nama, tarif, daya, kwh };
};

export default function Receipt() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const receiptRef = useRef(null);
  const [receiptData, setReceiptData] = useState(null);
  const [receiptSettings, setReceiptSettings] = useState(null);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const logoRef = useRef(null);

  useEffect(() => {
    // Get receipt data from localStorage
    const storedReceipt = localStorage.getItem("last_receipt");
    if (storedReceipt) {
      try {
        const data = JSON.parse(storedReceipt);
        setReceiptData(data);
      } catch (e) {
        console.error("Failed to parse receipt data");
        navigate("/");
      }
    } else {
      navigate("/");
    }
    // Fetch receipt settings
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/receipt-settings`)
      .then(r => r.json())
      .then(data => {
        setReceiptSettings(data);
        // If no logo, mark as loaded
        if (!data.logo_url) setLogoLoaded(true);
      })
      .catch(() => setLogoLoaded(true));
  }, [navigate]);

  // Preload logo image to ensure it's ready for html2canvas
  const handleLogoLoad = useCallback(() => {
    setLogoLoaded(true);
  }, []);

  const handleLogoError = useCallback(() => {
    console.error("Failed to load logo");
    setLogoLoaded(true);
  }, []);

  // Helper function to ensure all images are loaded before capture
  const waitForImagesToLoad = async (element) => {
    const images = element.querySelectorAll('img');
    const loadPromises = [];
    
    for (const img of images) {
      if (img.complete && img.naturalHeight !== 0) {
        continue; // Already loaded
      }
      loadPromises.push(
        new Promise((resolve) => {
          const handleLoad = () => {
            img.removeEventListener('load', handleLoad);
            img.removeEventListener('error', handleLoad);
            resolve();
          };
          img.addEventListener('load', handleLoad);
          img.addEventListener('error', handleLoad);
          // Fallback timeout
          setTimeout(handleLoad, 3000);
        })
      );
    }
    
    await Promise.all(loadPromises);
    // Extra delay for browser to finish rendering
    await new Promise(r => setTimeout(r, 300));
  };

  // Prepare images for capture - preload all base64 images
  const prepareForCapture = async () => {
    if (receiptSettings?.logo_url) {
      try {
        await preloadImageAsBase64(receiptSettings.logo_url);
      } catch (e) {
        console.warn('Failed to preload logo:', e);
      }
    }
    // Wait a bit more for rendering
    await new Promise(r => setTimeout(r, 300));
  };

  const handleClose = () => {
    // Clear receipt data and navigate back
    localStorage.removeItem("last_receipt");
    // Get return URL or default to home
    const returnUrl = searchParams.get("return") || "/";
    navigate(returnUrl);
  };

  const handlePrintRawBT = async () => {
    if (!receiptData) return;
    const d = "================================";
    const f = (n) => `Rp${(n || 0).toLocaleString("id-ID")}`;
    const headerText = receiptSettings?.header_text || "PRODUK DIGITAL";
    const showFee = receiptSettings?.show_fee_loket !== false;
    const footerText = receiptSettings?.footer_text || "Terima kasih atas pembayaran Anda";
    const noteText = receiptSettings?.note_text || "Struk ini adalah bukti pembayaran yang sah";
    let r = `     ${headerText}\n`;
    r += `     Struk Pembayaran\n${d}\nTanggal  : ${receiptData.date}\n${d}\n`;
    const isPdam = receiptData.service_type === "pdam" || receiptData.service_type === "pdam_tebo";
    if (isPdam && receiptData.bill_details?.length > 0) {
      r += `No.Samb  : ${receiptData.customer_no}\nNama     : ${receiptData.customer_name}\n`;
      if (receiptSettings?.show_customer_address !== false && receiptData.customer_address) r += `Alamat   : ${receiptData.customer_address}\n`;
      r += `${d}\n`;
      receiptData.bill_details.forEach((b) => {
        const periode = b.bulan || b.periode_text || b.periode || "";
        const fee = b.app_admin_fee !== undefined ? b.app_admin_fee : b.admin_fee;
        r += `Periode  : ${periode}\n`;
        if (fee > 0) r += `Biaya Adm: ${f(fee)}\n`;
        r += `SUBTOTAL : ${f(b.total || b.total_bayar)}\n${d}\n`;
      });
    } else {
      r += `Layanan  : ${receiptData.service_name}\nNo.Plg   : ${receiptData.customer_no}\n`;
      if (receiptData.customer_name) r += `Nama     : ${receiptData.customer_name}\n`;
      r += `${d}\nProduk   : ${receiptData.product_name}\nHarga    : ${f(receiptData.amount)}\n`;
      if (showFee) r += `Biaya Adm: ${f(receiptData.admin_fee)}\n`;
      r += `${d}\n`;
      if (receiptData.sn) {
        if (receiptData.service_type === "token_listrik") {
          const parsed = parsePlnTokenSN(receiptData.sn);
          r += `\n*** TOKEN LISTRIK ***\n${d}\nNo.Token : ${parsed.token}\n`;
          if (parsed.nama) r += `Nama     : ${parsed.nama}\n`;
          if (parsed.tarif) r += `Tarif    : ${parsed.tarif}\n`;
          if (parsed.daya) r += `Daya     : ${parsed.daya}\n`;
          if (parsed.kwh) r += `kWh      : ${parsed.kwh}\n`;
          r += `${d}\n`;
        } else {
          r += `SN/Token : ${receiptData.sn}\n${d}\n`;
        }
      }
    }
    // Only show Total Bayar if show_total_tagihan is enabled (default true)
    if (receiptSettings?.show_total_tagihan !== false) {
      r += `Total Tagihan: ${f(receiptData.bill_details?.reduce((sum, b) => sum + (b.total_tagihan || b.jumlah || 0), 0) || receiptData.amount)}\n`;
      r += `TOTAL BAYAR: ${f(receiptData.total)}\n`;
    }
    r += `${d}\nStatus   : ${receiptData.status}\n`;
    if (!receiptData.hide_cashier && receiptSettings?.show_kasir !== false) r += `Kasir    : ${receiptData.cashier_name}\n`;
    r += `${d}\n  ${footerText}\n ${noteText}\n\n\n\n`;
    await printReceiptSmart(r, toast);
  };

  const handleDownloadReceipt = async () => {
    if (!receiptRef.current) return;
    
    toast.info("Menyiapkan struk...");
    
    // Prepare images for capture
    await waitForImagesToLoad(receiptRef.current);
    await prepareForCapture();
    
    try {
      const canvas = await html2canvas(receiptRef.current, { 
        scale: 2, 
        backgroundColor: "#ffffff",
        useCORS: true,
        allowTaint: true,
        logging: false,
        imageTimeout: 0, // No timeout - wait for all images
        onclone: (clonedDoc, element) => {
          // Ensure cloned images have the same src
          const originalImages = receiptRef.current.querySelectorAll('img');
          const clonedImages = element.querySelectorAll('img');
          originalImages.forEach((origImg, i) => {
            if (clonedImages[i] && origImg.src) {
              clonedImages[i].src = origImg.src;
            }
          });
        }
      });
      const link = document.createElement("a");
      link.download = `struk-${(receiptData?.id||"").slice(0,8).toUpperCase()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Struk diunduh!");
    } catch (err) { 
      console.error("Download error:", err);
      toast.error("Gagal unduh"); 
    }
  };

  const handleShareReceipt = async () => {
    if (!receiptRef.current) return;
    
    toast.info("Menyiapkan struk...");
    
    // Prepare images for capture
    await waitForImagesToLoad(receiptRef.current);
    await prepareForCapture();
    
    try {
      const canvas = await html2canvas(receiptRef.current, { 
        scale: 2, 
        backgroundColor: "#ffffff",
        useCORS: true,
        allowTaint: true,
        logging: false,
        imageTimeout: 0, // No timeout - wait for all images
        onclone: (clonedDoc, element) => {
          // Ensure cloned images have the same src
          const originalImages = receiptRef.current.querySelectorAll('img');
          const clonedImages = element.querySelectorAll('img');
          originalImages.forEach((origImg, i) => {
            if (clonedImages[i] && origImg.src) {
              clonedImages[i].src = origImg.src;
            }
          });
        }
      });
      canvas.toBlob(async (blob) => {
        if (navigator.share && blob) {
          const file = new File([blob], `struk.png`, { type: "image/png" });
          await navigator.share({ title: "Struk Pembayaran", files: [file] });
        } else { 
          // Fallback: download instead
          const link = document.createElement("a");
          link.download = `struk-${(receiptData?.id||"").slice(0,8).toUpperCase()}.png`;
          link.href = canvas.toDataURL("image/png");
          link.click();
          toast.success("Struk diunduh!");
        }
      });
    } catch (err) { 
      console.error("Share error:", err);
      toast.error("Gagal share"); 
    }
  };

  if (!receiptData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-100 to-sky-50 flex items-center justify-center">
        <div className="spinner w-8 h-8 border-sky-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 to-sky-50">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-lg mx-auto"
      >
        {/* Header with close button */}
        <div className="sticky top-0 z-10 bg-gradient-to-b from-sky-100 to-transparent pt-4 px-4 pb-2">
          <button 
            onClick={handleClose} 
            className="w-12 h-12 rounded-full bg-white shadow-lg hover:bg-slate-50 flex items-center justify-center ml-auto"
          >
            <X className="w-6 h-6 text-slate-600" />
          </button>
        </div>

        {/* Receipt content */}
        <div className="px-4 pb-8">
          <div ref={receiptRef} className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-4 sm:p-5 mb-6 shadow-sm">
            <div className="text-center mb-4 pb-3 border-b border-dashed border-slate-300">
              {receiptSettings?.logo_url ? (
                <div className="flex justify-center mb-2">
                  <img 
                    ref={logoRef}
                    src={receiptSettings.logo_url} 
                    alt="Logo" 
                    onLoad={handleLogoLoad}
                    onError={handleLogoError}
                    style={{
                      maxWidth: `${LOGO_MAX_WIDTH}px`,
                      maxHeight: `${LOGO_MAX_HEIGHT}px`,
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain'
                    }}
                  />
                </div>
              ) : (
                <img src="/icons/icon-72x72.png" alt="Logo" className="w-12 h-12 mx-auto mb-2 rounded-lg" />
              )}
              <h3 className="text-xl font-heading font-bold text-slate-800">{receiptSettings?.header_text || "PRODUK DIGITAL"}</h3>
              <p className="text-sm text-slate-500">Struk Pembayaran</p>
            </div>
            
            <div className="space-y-2 text-sm mb-4 pb-3 border-b border-dashed border-slate-300">
              <div className="flex justify-between">
                <span className="text-slate-500">Tanggal</span>
                <span className="font-medium text-xs">{receiptData.date}</span>
              </div>
            </div>
            
            <div className="space-y-2 text-sm mb-4 pb-3 border-b border-dashed border-slate-300">
              <div className="flex justify-between">
                <span className="text-slate-500">{(receiptData.service_type === "pdam" || receiptData.service_type === "pdam_tebo") ? "No. Sambungan" : "No. Pelanggan"}</span>
                <span className="font-mono font-bold">{receiptData.customer_no}</span>
              </div>
            </div>

            {/* PDAM / PDAM Tebo Bill Details */}
            {(receiptData.service_type === "pdam" || receiptData.service_type === "pdam_tebo") && receiptData.bill_details?.length > 0 ? (
              <div className="space-y-3 mb-4">
                {receiptData.bill_details.map((bill, idx) => (
                  <div key={idx} className="bg-slate-50 rounded-lg border border-slate-200 p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">{receiptData.customer_name}</p>
                        {receiptSettings?.show_customer_address !== false && receiptData.customer_address && <p className="text-xs text-slate-500">{receiptData.customer_address}</p>}
                      </div>
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">LUNAS</span>
                    </div>
                    <div className="flex justify-between items-end pt-2">
                      <div>
                        <p className="text-xs text-slate-400">Periode</p>
                        <p className="font-medium text-sm">{bill.bulan || bill.periode_text || bill.periode}</p>
                        {(bill.app_admin_fee ?? bill.admin_fee) > 0 && (
                          <p className="text-xs text-sky-600 font-medium mt-1">Biaya Admin: {formatCurrency(bill.app_admin_fee ?? bill.admin_fee)}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">Subtotal</p>
                        <p className="text-lg font-bold text-sky-600">{formatCurrency(bill.total || bill.total_bayar)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2 text-sm mb-4">
                {receiptData.customer_name && <div className="flex justify-between"><span className="text-slate-500">Nama</span><span className="font-medium">{receiptData.customer_name}</span></div>}
                <div className="flex justify-between"><span className="text-slate-500">Produk</span><span className="font-medium text-right max-w-[60%] text-xs">{receiptData.product_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Harga</span><span className="font-medium">{formatCurrency(receiptData.amount)}</span></div>
                {receiptSettings?.show_fee_loket !== false && (
                  <div className="flex justify-between"><span className="text-sky-600 font-medium">Biaya Admin</span><span className="text-sky-600 font-medium">{formatCurrency(receiptData.admin_fee)}</span></div>
                )}
                
                {/* SN/Token for token_listrik and pulsa */}
                {(receiptData.service_type === "token_listrik" || receiptData.service_type === "pulsa") && (
                  <div className={`rounded-lg p-3 mt-3 ${receiptData.sn ? 'bg-green-50 border-2 border-green-400' : 'bg-orange-50 border border-orange-300'}`}>
                    {receiptData.sn ? (
                      <>
                        <div className="text-center mb-2">
                          <span className="text-green-700 font-bold text-sm">
                            {receiptData.service_type === "token_listrik" ? "TOKEN LISTRIK PLN" : "SERIAL NUMBER"}
                          </span>
                        </div>
                        {receiptData.service_type === "token_listrik" ? (() => {
                          const parsed = parsePlnTokenSN(receiptData.sn);
                          return (
                            <div className="space-y-2">
                              <div className="bg-white rounded-lg p-3 border-2 border-green-400 shadow-sm">
                                <p className="text-xs text-green-600 text-center mb-1 font-medium">No. Token (Masukkan ke Meter)</p>
                                <p className="font-mono font-bold text-green-800 text-xl text-center select-all tracking-widest">
                                  {parsed.token}
                                </p>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                {parsed.nama && (
                                  <div className="bg-white/70 rounded p-2 col-span-2">
                                    <p className="text-green-600 font-medium">Nama Pelanggan</p>
                                    <p className="font-semibold text-green-800">{parsed.nama}</p>
                                  </div>
                                )}
                                {parsed.tarif && (
                                  <div className="bg-white/70 rounded p-2">
                                    <p className="text-green-600 font-medium">Tarif</p>
                                    <p className="font-semibold text-green-800">{parsed.tarif}</p>
                                  </div>
                                )}
                                {parsed.daya && (
                                  <div className="bg-white/70 rounded p-2">
                                    <p className="text-green-600 font-medium">Daya</p>
                                    <p className="font-semibold text-green-800">{parsed.daya}</p>
                                  </div>
                                )}
                                {parsed.kwh && (
                                  <div className="bg-white/70 rounded p-2 col-span-2">
                                    <p className="text-green-600 font-medium">Jumlah kWh</p>
                                    <p className="font-semibold text-green-800 text-base">{parsed.kwh}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })() : (
                          <div className="bg-white rounded-lg p-3 border border-green-300">
                            <p className="font-mono font-bold text-green-800 text-center select-all break-all">
                              {receiptData.sn}
                            </p>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-orange-700 font-medium">
                            {receiptData.service_type === "token_listrik" ? "TOKEN:" : "SN:"}
                          </span>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-orange-500 animate-pulse" />
                            <span className="text-orange-600 text-sm">Menunggu...</span>
                          </div>
                        </div>
                        <p className="text-xs text-orange-600 mt-2">
                          Token sedang diproses. Cek kembali di Riwayat Transaksi.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Total - Only show if show_total_tagihan is enabled */}
            {receiptSettings?.show_total_tagihan !== false && (
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <div><span className="font-semibold text-slate-800">Total Bayar</span><p className="text-xs text-slate-500">Sudah termasuk biaya admin</p></div>
                  <span className="text-xl font-heading font-bold text-sky-600">{formatCurrency(receiptData.total)}</span>
                </div>
              </div>
            )}
            
            {/* Status */}
            <div className="flex justify-between items-center mt-3 pt-3 border-t border-dashed border-slate-300">
              <span className="text-sm text-slate-500">Status</span>
              {receiptData.isPending ? (
                <span className="px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1 bg-orange-100 text-orange-700">
                  <Clock className="w-4 h-4" /> PENDING
                </span>
              ) : receiptData.status === "GAGAL" ? (
                <span className="px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1 bg-red-100 text-red-700">
                  <X className="w-4 h-4" /> GAGAL
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1 bg-emerald-100 text-emerald-700">
                  {receiptData.status}<Check className="w-4 h-4" />
                </span>
              )}
            </div>
            
            {/* Kasir */}
            {!receiptData.hide_cashier && (
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-dashed border-slate-300">
                <span className="text-sm text-slate-500">Kasir</span>
                <span className="text-sm font-semibold text-slate-800">{receiptData.cashier_name} ({receiptData.cashier_role})</span>
              </div>
            )}
            
            {/* Footer */}
            <div className="text-center mt-4 pt-3 border-t border-dashed border-slate-300">
              <p className="text-xs text-slate-500">Terima kasih atas pembayaran Anda</p>
              <p className="text-xs text-slate-400">Struk ini adalah bukti pembayaran yang sah</p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-3">
            <Button onClick={handlePrintRawBT} className="w-full h-14 rounded-xl gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold" data-testid="receipt-print-btn">
              <Printer className="w-5 h-5" /> Print Struk (Bluetooth)
            </Button>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" onClick={handleDownloadReceipt} className="h-11 rounded-xl gap-2 text-sm bg-white">
                <Download className="w-4 h-4" /> Unduh PNG
              </Button>
              <Button variant="outline" onClick={handleShareReceipt} className="h-11 rounded-xl gap-2 text-sm bg-white">
                <Share2 className="w-4 h-4" /> Share
              </Button>
            </div>
            <Button className="w-full rounded-full h-12" variant="ghost" onClick={handleClose}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Selesai
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
